import React, { forwardRef } from 'react'
import type { FirearmBuild } from '../../app/models/build'
import { buildPath } from '../../app/utils/buildPath'
import GunSlopWordmark from '../../app/common/ui/GunSlopWordmark'

// ── Inline hex palette (html2canvas cannot resolve CSS custom properties) ──────
const C = {
  bg: '#0A0E13',
  surface: '#161B22',
  surface2: '#1C2128',
  border: '#30363D',
  text: '#E6EDF3',
  muted: '#8B949E',
  accent: '#E85D04',
  gold: '#C9A84C',
  brand: '#E85D04'
} as const

interface Props {
  build: FirearmBuild;
  /** Optional cover photo URL to show on the card. */
  heroUrl?: string | null;
}

function fmtCurrency (v: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(v)
}

const ZONE_ORDER = [
  'Muzzle Device', 'Barrel', 'Handguard / Rail', 'Bolt Carrier Group',
  'Charging Handle', 'Upper Receiver', 'Optic', 'Light / Laser',
  'Trigger Group', 'Lower Receiver', 'Pistol Grip', 'Stock / Brace',
  'Sling / Mount', 'Other'
]

/**
 * 1200×630 Open Graph share card rendered as a real DOM node so that
 * html2canvas can rasterise it.
 *
 * Must be rendered off-screen (position: fixed, left: -9999px) so it
 * doesn't affect layout, but IS part of the DOM so html2canvas can walk it.
 */
const BuildShareCard = forwardRef<HTMLDivElement, Props>(function BuildShareCard (
  { build, heroUrl },
  ref
) {
  const totalValue = build.parts?.reduce(
    (s, bp) => s + (bp.part?.value ?? 0) * (bp.quantity ?? 1), 0
  ) ?? 0

  const partCount = build.parts?.length ?? 0

  // Build a flat ordered list of parts for the right panel (max ~10 to fit)
  const orderedParts = ZONE_ORDER
    .flatMap((z) => build.partsByZone?.[z] ?? [])
    .slice(0, 10)

  return (
    <div
      ref={ref}
      style={{
        position: 'fixed',
        left: -9999,
        top: 0,
        width: 1200,
        height: 630,
        background: C.bg,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'DM Sans, sans-serif',
        overflow: 'hidden',
        zIndex: -1
      }}
    >
      {/* ── Top accent bar ── */}
      <div style={{ height: 4, background: C.accent, flexShrink: 0 }} />

      {/* ── Body ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Left panel — cover image or schematic placeholder */}
        <div
          style={{
            width: 480,
            flexShrink: 0,
            background: C.surface2,
            borderRight: `1px solid ${C.border}`,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {heroUrl ? (
            <img
              src={heroUrl}
              alt={build.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              crossOrigin="anonymous"
            />
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
                color: C.muted
              }}
            >
              {/* Simple firearm silhouette stand-in */}
              <div
                style={{
                  width: 180,
                  height: 60,
                  border: `2px solid ${C.border}`,
                  borderRadius: 8,
                  background: C.surface,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'Oswald, sans-serif',
                  fontWeight: 700,
                  fontSize: 18,
                  color: C.muted,
                  letterSpacing: 2
                }}
              >
                {build.firearmModelPlatform ?? 'BUILD'}
              </div>
            </div>
          )}
        </div>

        {/* Right panel — build info */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: '32px 36px 24px',
            overflow: 'hidden'
          }}
        >
          {/* Platform badge */}
          {build.firearmModelPlatform && (
            <div
              style={{
                display: 'inline-flex',
                alignSelf: 'flex-start',
                background: 'rgba(232,93,4,0.15)',
                color: C.accent,
                border: '1px solid rgba(232,93,4,0.4)',
                borderRadius: 4,
                padding: '2px 8px',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: 'uppercase',
                marginBottom: 10,
                fontFamily: 'Oswald, sans-serif'
              }}
            >
              {build.firearmModelPlatform}
            </div>
          )}

          {/* Build name */}
          <div
            style={{
              fontFamily: 'Oswald, sans-serif',
              fontWeight: 700,
              fontSize: 36,
              color: C.text,
              lineHeight: 1.1,
              marginBottom: 6,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical'
            }}
          >
            {build.name}
          </div>

          {/* Model + owner */}
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
            {build.firearmModelName} · by {build.ownerUsername}
          </div>

          {/* Stats row */}
          <div style={{ display: 'flex', gap: 24, marginBottom: 24 }}>
            <div>
              <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 2 }}>Parts</div>
              <div style={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 24, color: C.text }}>{partCount}</div>
            </div>
            {totalValue > 0 && (
              <div>
                <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 2 }}>Est. Value</div>
                <div style={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 24, color: C.gold }}>{fmtCurrency(totalValue)}</div>
              </div>
            )}
            {typeof build.voteScore === 'number' && (
              <div>
                <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 2 }}>Score</div>
                <div
                  style={{
                    fontFamily: 'Oswald, sans-serif',
                    fontWeight: 700,
                    fontSize: 24,
                    color: build.voteScore > 0 ? '#3FB950' : build.voteScore < 0 ? '#F85149' : C.muted
                  }}
                >
                  {build.voteScore > 0 ? '+' : ''}{build.voteScore}
                </div>
              </div>
            )}
          </div>

          {/* Parts list */}
          {orderedParts.length > 0 && (
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <div
                style={{
                  fontSize: 10,
                  color: C.muted,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  fontWeight: 700,
                  marginBottom: 8
                }}
              >
                Components
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {orderedParts.map((bp) => (
                  <div
                    key={bp.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 12
                    }}
                  >
                    <div
                      style={{
                        width: 3,
                        height: 3,
                        borderRadius: '50%',
                        background: C.accent,
                        flexShrink: 0
                      }}
                    />
                    <span style={{ color: C.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}>
                      {bp.part?.name ?? 'Part'}
                    </span>
                    {bp.part?.manufacturer && (
                      <span style={{ color: C.muted, fontSize: 11, flexShrink: 0 }}>
                        {bp.part.manufacturer}
                      </span>
                    )}
                  </div>
                ))}
                {(build.parts?.length ?? 0) > 10 && (
                  <div style={{ fontSize: 11, color: C.muted, fontStyle: 'italic', marginTop: 2 }}>
                    +{(build.parts?.length ?? 0) - 10} more…
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Footer ── */}
      <div
        style={{
          height: 40,
          flexShrink: 0,
          background: C.surface,
          borderTop: `1px solid ${C.border}`,
          display: 'flex',
          alignItems: 'center',
          padding: '0 24px',
          gap: 8
        }}
      >
        <GunSlopWordmark
          size="xs"
          slopColor={C.accent}
          sx={{ fontSize: 14, letterSpacing: 1, color: C.text }}
        />
        <div style={{ width: 1, height: 14, background: C.border }} />
        <div style={{ fontSize: 12, color: C.muted }}>
          gunslop.com{buildPath(build).replace(/^\//, '')}
        </div>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: C.muted, fontStyle: 'italic' }}>
          Built by the community
        </div>
      </div>
    </div>
  )
})

export default BuildShareCard
